import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLocations, getBookings, getTestimonials, getGalleryItems, getContactMessages } from '../services/api';
import { 
  MapPin, 
  Calendar, 
  MessageSquare, 
  Image, 
  TrendingUp,
  Users,
  DollarSign,
  Activity
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    locations: 0,
    bookings: 0,
    testimonials: 0,
    gallery: 0,
    contacts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [locations, bookings, testimonials, gallery, contacts] = await Promise.all([
        getLocations(),
        getBookings(),
        getTestimonials(),
        getGalleryItems(),
        getContactMessages()
      ]);

      setStats({
        locations: locations.length,
        bookings: bookings.length,
        testimonials: testimonials.length,
        gallery: gallery.length,
        contacts: contacts.length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Locations',
      value: stats.locations,
      icon: MapPin,
      color: 'blue',
      link: '/admin/locations'
    },
    {
      title: 'Total Bookings',
      value: stats.bookings,
      icon: Calendar,
      color: 'green',
      link: '/admin/bookings'
    },
    {
      title: 'Testimonials',
      value: stats.testimonials,
      icon: MessageSquare,
      color: 'purple',
      link: '/admin/testimonials'
    },
    {
      title: 'Gallery Items',
      value: stats.gallery,
      icon: Image,
      color: 'pink',
      link: '/admin/gallery'
    },
    {
      title: 'Contact Messages',
      value: stats.contacts,
      icon: MessageSquare,
      color: 'yellow',
      link: '/admin/contacts'
    },
    {
      title: 'Total Revenue',
      value: '₹' + (stats.bookings * 3500).toLocaleString(),
      icon: DollarSign,
      color: 'emerald',
      link: '/admin/bookings'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl p-6 text-black">
        <h1 className="text-3xl font-black mb-2">Welcome to Admin Panel</h1>
        <p className="text-lg">Manage your Indya Bungy website with ease</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const colorClasses = {
            blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
            green: 'from-green-500/20 to-green-600/20 border-green-500/30',
            purple: 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
            pink: 'from-pink-500/20 to-pink-600/20 border-pink-500/30',
            yellow: 'from-yellow-500/20 to-yellow-600/20 border-yellow-500/30',
            emerald: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30'
          };

          return (
            <Link
              key={index}
              to={stat.link}
              className={`bg-gradient-to-br ${colorClasses[stat.color]} border backdrop-blur-sm rounded-xl p-6 hover:scale-105 transition-transform duration-300`}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className="text-white" size={32} />
                <TrendingUp className="text-green-400" size={20} />
              </div>
              <div className="text-3xl font-black text-white mb-1">{stat.value}</div>
              <div className="text-gray-300 font-medium">{stat.title}</div>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/admin/videos"
              className="block bg-gray-700 hover:bg-gray-600 rounded-lg p-4 text-white transition-colors"
            >
              <div className="font-bold mb-1">Manage Videos</div>
              <div className="text-sm text-gray-400">Update hero and location videos</div>
            </Link>
            <Link
              to="/admin/coupons"
              className="block bg-gray-700 hover:bg-gray-600 rounded-lg p-4 text-white transition-colors"
            >
              <div className="font-bold mb-1">Create Coupons</div>
              <div className="text-sm text-gray-400">Add new discount coupons</div>
            </Link>
            <Link
              to="/admin/blogs"
              className="block bg-gray-700 hover:bg-gray-600 rounded-lg p-4 text-white transition-colors"
            >
              <div className="font-bold mb-1">Write Blog</div>
              <div className="text-sm text-gray-400">Create new blog post</div>
            </Link>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">System Info</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Platform</span>
              <span className="text-white font-medium">Node.js + React</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Database</span>
              <span className="text-white font-medium">MongoDB</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Status</span>
              <span className="text-green-400 font-medium flex items-center">
                <Activity size={16} className="mr-1" />
                Online
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;